// Firestore persistence helpers — org-scoped schema
//
// The ORG owns all data. Users are tracked as fields, not path segments.
// This ensures data survives user departures and supports multi-user attribution.
//
// Schema:
//   /orgs/{orgId}/runs/{runId}                                     — run metadata
//     Fields: uid, userEmail, userQuery, selectedCategory, classifierResult,
//             generationSpec, routerCalls, mode, status, candidateCount, ts,
//             toolResults, createdAt, updatedAt,
//             reviewState, executedRows, executedBy, rankingWeights,
//             rowsReconstructed (editable plan 1a, below),
//             plannedSteps, budgetOverrides (legacy D8 drafts only)
//     Editable plan 1a (docs/superpowers/specs/2026-10-07-editable-plan-design.md):
//     reviewState is serializeReviewState() of the review reducer
//     (src/state/reviewStateStorage.js); it is the only draft source on
//     resume, and a draft without it is a legacy draft (plannedSteps +
//     budgetOverrides, rebuilt once by rebuildLegacyReview()). executedRows
//     are the rows that were dispatched (accepted, fitted, dispatchTarget
//     stamped), executedBy is the uid of the user who executed them (written
//     together with executedRows, never on a draft), and rankingWeights
//     travel with them. generationSpec is overwritten with the executed spec
//     at Execute. rowsReconstructed: true marks a Re-run whose rows were
//     rebuilt from a legacy plan; it is never written as false. startRun()
//     writes each of these, and the two legacy fields, only when the record
//     carries it. patchRun() replaces every field it is given whole (setDoc
//     mergeFields) and leaves every other field untouched.
//     status lifecycle (D1, 2026-08-20): startRun() writes 'running' the
//     moment a run begins, BEFORE any MatterGen/FM4M/GT4SD chunk is
//     dispatched, so a tab close mid-run leaves this doc, and whatever
//     patchRun() progress landed, durably in Firestore instead of nothing at
//     all. patchRun() writes toolResults/candidateCount as each step
//     settles, and again to flip status to 'complete' or 'error' when the
//     run finishes. A run doc stuck at 'running' with a stale updatedAt is
//     never rewritten by anyone (there is no backend orchestrator for
//     runs): src/utils/runStatus.js's deriveRunStatus() is the read side
//     that turns that staleness into an 'interrupted' render in
//     HistoryTable. A doc with no status field at all (every run written
//     before this change) is treated as 'complete', unambiguous, and the
//     only value that renders exactly as it always has; pushRun()'s atomic
//     writes carry 'completed', which renders the same way.
//     D8 (2026-08-20) added an earlier rung on this same ladder: startRun()
//     also accepts status 'draft', written the MOMENT the NL decomposer's
//     plan is produced, before the user has reviewed planned steps or
//     touched budget splits, let alone clicked Generate. patchRun() writes
//     the user's edits during that review window, debounced client-side
//     (budgetOverrides before editable plan 1a, reviewState from 1a).
//     AppProvider.jsx's executePlannedSteps() then PROMOTES that same doc to
//     'running' with one patchRun() when Generate is clicked -- reusing the
//     id, never writing a second doc for the same run. A draft doc that's
//     never promoted (the user abandoned the review) just sits there;
//     src/utils/runStatus.js's isStaleDraft() is the read-side rule
//     (HistoryTable.jsx) that hides one older than 7 days, nothing ever
//     deletes it. Apart from reviewState, which only a reviewed run carries,
//     every field a promoted draft ends up with is identical in shape to a
//     run that was always startRun()'d as 'running' directly (the
//     manual-mode and Re-run paths, which have no review window and so never
//     pass through 'draft' at all); only which write call flipped status
//     last differs.
//   /orgs/{orgId}/runs/{runId}/candidates/{fp}                     — candidates
//     Fields: fingerprint, uniqueKey, domain, score, rank, formula, smiles, n,
//             source (service, variant, model_path, weights, version), props, frame,
//             diagnostics, saved, savedBy, savedAt, orgId, runId
//   /orgs/{orgId}/analyses/{fp}/kinds/{kind}                       — analyses (cross-run)
//     Fields: kind, data, initiatedBy (uid), initiatedByEmail, runId, updatedAt
//   /orgs/{orgId}/notes/{fp}                                       — candidate notes (chat-style)
//     Fields: fingerprint, messages: [{ id, uid, userEmail, text, createdAt, editedAt? }]

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  runTransaction,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firestoreDb } from '../config/firebase';

const db = firestoreDb;

/** Simple djb2 hash — returns a hex string. */
function _djb2(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h.toString(16);
}

function toDocId(fingerprint) {
  const encoded = encodeURIComponent(fingerprint);
  if (encoded.length <= 500) return encoded;
  return encoded.slice(0, 400) + '_' + _djb2(encoded);
}

/** Stringify nested arrays for Firestore (which rejects array-of-arrays). */
function _maybeStringify(val) {
  if (val == null) return null;
  if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])) {
    return JSON.stringify(val);
  }
  return val;
}

/** Recursively walk an object and apply _maybeStringify to any array values. */
function _deepStringifyArrays(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return _maybeStringify(obj);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      out[k] = _maybeStringify(v);
    } else if (v && typeof v === 'object') {
      out[k] = _deepStringifyArrays(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Parse a field that may have been stringified for Firestore.
 *  Only parses strings that look like JSON arrays (start with `[[`) to avoid
 *  false positives on text fields that happen to start with `[`. */
function _maybeParse(val) {
  if (typeof val === 'string' && val.startsWith('[[')) {
    try { return JSON.parse(val); } catch (_) { return val; }
  }
  return val;
}

/** Reverse of _deepStringifyArrays — walk an object and parse any stringified arrays back. */
function _deepParseArrays(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => _deepParseArrays(item));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.startsWith('[[')) { // #47 — removed duplicate OR branch
      try { out[k] = JSON.parse(v); } catch (_) { out[k] = v; }
    } else if (v && typeof v === 'object') {
      out[k] = _deepParseArrays(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Recursively remove any nested arrays (array-of-arrays) from an object tree.
 * Firestore rejects documents that contain arrays whose elements are also arrays.
 * Flat arrays of primitives/objects are kept; arrays containing arrays are deleted.
 */
function _removeNestedArrays(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj; // caller decides whether to keep
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      // Keep only if NO element is itself an array
      if (v.some(el => Array.isArray(el))) continue; // drop this field entirely
      // Recurse into array elements that are objects
      out[k] = v.map(el => (el && typeof el === 'object' && !Array.isArray(el)) ? _removeNestedArrays(el) : el);
    } else if (v && typeof v === 'object') {
      out[k] = _removeNestedArrays(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Recursively stringify nested arrays so Firestore accepts the document.
 * Unlike _removeNestedArrays (which drops them), this preserves the data as
 * JSON strings prefixed with __JSON_ARRAY__ so loadRuns can restore them.
 */
function _stringifyNestedArrays(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      if (v.some(el => Array.isArray(el))) {
        out[k] = '__JSON_ARRAY__' + JSON.stringify(v);
      } else {
        out[k] = v.map(el => (el && typeof el === 'object' && !Array.isArray(el)) ? _stringifyNestedArrays(el) : el);
      }
    } else if (v && typeof v === 'object') {
      out[k] = _stringifyNestedArrays(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Reverse of _stringifyNestedArrays — restore __JSON_ARRAY__ strings back
 * to real arrays when loading from Firestore.
 */
function _restoreNestedArrays(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(el => _restoreNestedArrays(el));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.startsWith('__JSON_ARRAY__')) {
      try { out[k] = JSON.parse(v.slice(14)); continue; } catch (_) {}
    }
    if (Array.isArray(v)) {
      out[k] = v.map(el => _restoreNestedArrays(el));
    } else if (v && typeof v === 'object') {
      out[k] = _restoreNestedArrays(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Strip candidate arrays from toolResults and stringify nested arrays before
 * writing to the run doc. Firestore rejects nested arrays, so they are stored
 * as prefixed JSON strings and restored by loadRuns via _restoreNestedArrays.
 */
function _stripToolResults(results) {
  if (!results || typeof results !== 'object') return null;
  try {
    const stripped = {};
    for (const [key, entry] of Object.entries(results)) {
      if (!entry || typeof entry !== 'object') { stripped[key] = entry; continue; }
      const clone = { ...entry };
      // Remove candidates array — stored in subcollection
      delete clone.candidates;
      stripped[key] = _stringifyNestedArrays(clone);
    }
    return JSON.parse(JSON.stringify(stripped));
  } catch (_) {
    return null;
  }
}

// ── Run history ───────────────────────────────────────────────────────────────

/**
 * Push a run record. The org owns the run; uid is a field.
 *
 * @param {string} orgId
 * @param {Object} rec - Must include: id, ts, plan. Should also include:
 *   uid, userEmail, userQuery, selectedCategory, classifierResult,
 *   routerCalls, mode
 */
export async function pushRun(orgId, rec) {
  if (!db || !orgId) return;
  const plan = rec.plan ? JSON.parse(JSON.stringify(rec.plan)) : null;
  const genSpec = plan?.generation_spec || {};

  const runDoc = _removeNestedArrays({
    id:                rec.id,
    ts:                rec.ts,
    // Who initiated this run
    uid:               rec.uid || null,
    userEmail:         rec.userEmail || null,
    // The raw user query
    userQuery:         rec.userQuery || genSpec?.meta?.user_prompt_raw || '',
    // Stage 0: category chip (if selected)
    selectedCategory:  rec.selectedCategory || null,
    // Stage 1: domain classifier result { categories, domains, confidence }
    classifierResult:  rec.classifierResult || null,
    // Stage 2: full generation spec (the complete plan)
    generationSpec:    genSpec,
    // Router output: which service calls were dispatched
    routerCalls:       rec.routerCalls || [],
    // Mode: 'nl' or 'manual'
    mode:              rec.mode || 'nl',
    // Description for history list
    description:       rec.userQuery || genSpec?.meta?.user_prompt_raw || '',
    // Completion status
    status:            rec.status || 'completed',
    candidateCount:    rec.candidateCount || 0,
    // Tool results metadata (run IDs, file URLs, model info) — enables View Results from history.
    // Strip candidates (stored in subcollection) and nested arrays (Firestore rejects them).
    toolResults:       rec.results ? _stripToolResults(rec.results) : null,
  });
  runDoc.createdAt = serverTimestamp();

  await setDoc(doc(db, 'orgs', orgId, 'runs', rec.id), runDoc);
}

/**
 * D1: write a run doc the MOMENT a run starts: status 'running', the parsed
 * spec, uid, started_at, before any MatterGen/FM4M/GT4SD chunk is
 * dispatched. Same doc shape pushRun() writes (extends, does not fork, the
 * schema); loadRuns()/HistoryTable see it exactly like any other run doc,
 * just earlier, and with status:'running' until patchRun() flips it
 * terminal. Callers pair this with one or more patchRun() calls as the run
 * progresses and settles.
 *
 * @param {string} orgId
 * @param {Object} rec - Same shape as pushRun's rec, plus the optional
 *   editable-plan 1a fields reviewState, executedRows, executedBy,
 *   rankingWeights and rowsReconstructed, and the legacy D8 plannedSteps and
 *   budgetOverrides. Each of these is written only when the record carries
 *   it; none gets a default.
 */
export async function startRun(orgId, rec) {
  if (!db || !orgId || !rec?.id) return;
  const plan = rec.plan ? JSON.parse(JSON.stringify(rec.plan)) : null;
  const genSpec = plan?.generation_spec || {};

  const fields = {
    id:                rec.id,
    ts:                rec.ts,
    uid:               rec.uid || null,
    userEmail:         rec.userEmail || null,
    userQuery:         rec.userQuery || genSpec?.meta?.user_prompt_raw || '',
    selectedCategory:  rec.selectedCategory || null,
    classifierResult:  rec.classifierResult || null,
    generationSpec:    genSpec,
    routerCalls:       rec.routerCalls || [],
    mode:              rec.mode || 'nl',
    description:       rec.userQuery || genSpec?.meta?.user_prompt_raw || '',
    // D8: 'draft' for the plan-review window itself (rec.status set
    // explicitly by AppProvider.jsx's startRunDoc), 'running' by default for
    // every caller that never reviews a plan first (manual mode, Re-run).
    status:            rec.status || 'running',
    candidateCount:    0,
    toolResults:       null,
  };
  // Legacy D8 draft fields (plan-review steps and budget-split overrides).
  // Editable plan 1a retires both for new runs (a draft carries reviewState
  // instead), so they are written only when a record still carries them and
  // never get an empty default that could pass for a legacy draft.
  if (Array.isArray(rec.plannedSteps)) fields.plannedSteps = rec.plannedSteps;
  if (rec.budgetOverrides && typeof rec.budgetOverrides === 'object') fields.budgetOverrides = rec.budgetOverrides;
  // Editable plan 1a: the serialized review (drafts and the fallback start of
  // a failed Execute), the rows that ran, who ran them, the ranking weights,
  // and the legacy-rebuild marker of a Re-run. JSON-cloned so no undefined
  // reaches Firestore; _removeNestedArrays below drops any array of arrays.
  if (rec.reviewState && typeof rec.reviewState === 'object') fields.reviewState = JSON.parse(JSON.stringify(rec.reviewState));
  if (Array.isArray(rec.executedRows)) fields.executedRows = JSON.parse(JSON.stringify(rec.executedRows));
  if (rec.executedBy !== undefined) fields.executedBy = rec.executedBy || null;
  if (rec.rankingWeights && typeof rec.rankingWeights === 'object') fields.rankingWeights = JSON.parse(JSON.stringify(rec.rankingWeights));
  if (rec.rowsReconstructed === true) fields.rowsReconstructed = true;

  const runDoc = _removeNestedArrays(fields);
  runDoc.createdAt = serverTimestamp();
  runDoc.updatedAt = serverTimestamp();

  await setDoc(doc(db, 'orgs', orgId, 'runs', rec.id), runDoc);
}

/** A JSON-safe copy for a whole-object run-doc field: undefined dropped by the
 * JSON round trip, then any array of arrays dropped (Firestore rejects one). */
function _jsonField(value) {
  return _removeNestedArrays(JSON.parse(JSON.stringify(value)));
}

/**
 * D1: incrementally patch a run doc already created by startRun(), used
 * both for per-step progress updates while a run is in flight (toolResults +
 * candidateCount growing as each MatterGen/FM4M/GT4SD call settles) and for
 * the terminal status transition when the run finishes or fails. Each field
 * passed replaces that field whole (setDoc mergeFields) and every field not
 * passed stays untouched, so a progress patch can never clobber fields
 * (userQuery, generationSpec, ...) written at startRun() time, and an edited
 * generationSpec or reviewState overwrites the stored one instead of
 * deep-merging into it (a { merge: true } deep merge would keep a nested key
 * the expert removed). Firestore write points must not meaningfully slow a
 * run: every call site in AppProvider.jsx fires this fire-and-forget,
 * tolerant of failure, the same discipline as every other write in this file.
 *
 * @param {string} orgId
 * @param {string} runId
 * @param {Object} patch - Any of: status, toolResults, candidateCount, routerCalls,
 *   budgetOverrides (legacy D8 drafts), and the editable-plan 1a fields
 *   generationSpec, reviewState, rankingWeights, executedRows and executedBy.
 *   Only keys actually passed are written; omit a key to leave it untouched.
 *   Any other key is dropped.
 */
export async function patchRun(orgId, runId, {
  status, toolResults, candidateCount, routerCalls, budgetOverrides,
  generationSpec, reviewState, rankingWeights, executedRows, executedBy,
} = {}) {
  if (!db || !orgId || !runId) return;
  const patch = { updatedAt: serverTimestamp() };
  if (status !== undefined) patch.status = status;
  if (toolResults !== undefined) patch.toolResults = toolResults ? _stripToolResults(toolResults) : null;
  if (candidateCount !== undefined) patch.candidateCount = candidateCount;
  if (routerCalls !== undefined) patch.routerCalls = routerCalls;
  if (budgetOverrides !== undefined) patch.budgetOverrides = budgetOverrides || {};
  if (generationSpec !== undefined) patch.generationSpec = _jsonField(generationSpec);
  if (reviewState !== undefined) patch.reviewState = _jsonField(reviewState);
  if (rankingWeights !== undefined) patch.rankingWeights = JSON.parse(JSON.stringify(rankingWeights));
  if (executedRows !== undefined) {
    // _removeNestedArrays returns an array argument unchanged, so the rows go
    // through it inside a wrapper object, which recurses into each row. A value
    // that is itself an array of arrays is dropped there and not written.
    const rows = _removeNestedArrays({ executedRows: JSON.parse(JSON.stringify(executedRows)) }).executedRows;
    if (rows !== undefined) patch.executedRows = rows;
  }
  if (executedBy !== undefined) patch.executedBy = executedBy || null;
  await setDoc(doc(db, 'orgs', orgId, 'runs', runId), patch, { mergeFields: Object.keys(patch) });
}

/**
 * Load all runs for an org (most recent first).
 */
export async function loadRuns(orgId) {
  if (!db || !orgId) return [];
  const q = query(
    collection(db, 'orgs', orgId, 'runs'),
    orderBy('ts', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const run = d.data();
    // Restore nested arrays that were stringified for Firestore storage
    if (run.toolResults) run.toolResults = _restoreNestedArrays(run.toolResults);
    return run;
  });
}

/**
 * Load run stats for an org — lightweight projection for usage dashboards.
 * Returns up to 500 most-recent runs with only the fields needed for aggregation.
 *
 * D8 review fix (durability audit 2026-08-20, batch C review IMPORTANT-1): a
 * draft run doc (status 'draft', written the moment a plan is produced, not
 * when it's actually executed) is real for History's purposes but must never
 * count as a "run" here: AdminPortal.jsx's Total Runs, per-user run share,
 * weekly/monthly counts, and churn-risk "last active" timestamp all read
 * this array directly. Filtered client-side, not via a Firestore `!=` query:
 * `!=` never matches a document missing the field at all, and every run
 * written before D8 has no `status` field, so a query-level filter would
 * have silently dropped all historical (pre-D8) runs from every admin
 * metric. Filtering the mapped array afterward correctly treats "no status"
 * as "not a draft" (kept) while excluding only an actual 'draft'.
 * loadRuns() (History's own data source) is intentionally left unfiltered:
 * drafts must stay visible there as Draft-badged, Resume-able rows.
 */
export async function loadRunStats(orgId) {
  if (!db || !orgId) return [];
  const q = query(
    collection(db, 'orgs', orgId, 'runs'),
    orderBy('ts', 'desc'),
    limit(500)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => {
      const r = d.data();
      return {
        ts: r.ts,
        uid: r.uid,
        userEmail: r.userEmail,
        candidateCount: r.candidateCount || 0,
        mode: r.mode,
        selectedCategory: r.selectedCategory || null,
        status: r.status,
      };
    })
    .filter(r => r.status !== 'draft');
}

// ── Run candidates ───────────────────────────────────────────────────────────

/**
 * Compute the canonical fingerprint for a candidate.
 * Must match _fingerprint() in AppProvider and _fpFrame() in AppShell.
 * Lattice-based: formula_L:a_b_c. Fallback: formula_N:atomCount. Molecules: mol_smiles.
 */
function _canonicalFingerprint(c) {
  // Molecule — H13: check the frame too; saveCandidate's candidateDoc carries
  // smiles only inside `frame`, and falling through to the last resort produced
  // the literal doc ID "undefined" for every saved molecule.
  const smiles = c.smiles || c.frame?.smiles;
  if (smiles) return `mol_${smiles}`;
  // Crystal with lattice parameters
  const abc = c.lattice_abc || c.frame?.lattice_abc;
  const formula = c.formula || null;
  if (formula && abc && abc.length === 3) {
    const [a, b, cc] = abc.map(v => (typeof v === 'number' ? v.toFixed(2) : v));
    return `${formula}_L:${a}_${b}_${cc}`;
  }
  // Fallback
  const n = c.frame?.n || c.n || 0;
  if (formula) return `${formula}_N:${n}`;
  // Last resort — adapter key; null (never undefined) when nothing resolves
  return c.fingerprint || c.uniqueKey || c.id || null;
}

/**
 * Map parsed result frames (crystal extxyz frames or molecule rows) to candidate
 * docs for bulk persistence into runs/{runId}/candidates — the flywheel's raw
 * feedstock. Mirrors the candidateDoc shape saveCandidate builds in AppProvider,
 * so a bulk-captured row and a hand-saved row are interchangeable.
 * Frames that can't be fingerprinted (no formula and no smiles) are dropped —
 * pushRunCandidates would refuse them anyway (H13).
 * Every doc is marked pooledCapture: true. This builder serves bulk capture only
 * (AppProvider.persistGeneratedCandidates), and firestore.rules admits a
 * pooledCapture doc only in an org whose doc records dataPooling true (spec
 * 2026-10-07 Part 0.5). Hand-saved candidates never pass through here.
 */
function framesToCandidateDocs(frames, { cifTexts = {}, source = null } = {}) {
  return (frames || []).map(frame => {
    if (!frame) return null;
    if (frame.type === 'molecule' || frame.smiles) {
      if (!frame.smiles) return null;
      return {
        smiles: frame.smiles,
        n: frame.n || null,
        score: frame.score ?? null,
        rank: frame.rank ?? null,
        source: frame.source || source,
        pooledCapture: true,
        frame,
      };
    }
    if (!frame.formula) return null;
    const elements = frame.atomLines?.map(l => (l.trim().split(/\s+/)[0] || '').match(/^[A-Z][a-z]?/)?.[0]).filter(Boolean) || frame.elements || [];
    const positions = frame.atomLines?.map(l => { const t = l.trim().split(/\s+/); return [Number(t[1]), Number(t[2]), Number(t[3])]; }) || frame.positions || [];
    return {
      formula: frame.formula,
      smiles: null,
      n: frame.n || elements.length || null,
      elements: elements.length > 0 ? elements : null,
      positions: positions.length > 0 ? positions : null,
      lattice_abc: frame.lattice_abc || null,
      lattice_matrix: frame.lattice_matrix || null,
      space_group: frame.spaceGroup || frame.space_group || null,
      composition: frame.composition || null,
      cif: cifTexts[frame.index] || frame.cif || null,
      score: frame.score ?? null,
      rank: frame.rank ?? null,
      source: frame.source || source,
      pooledCapture: true,
      frame,
    };
  }).filter(Boolean).map(withFrameFileUrls);
}
export { framesToCandidateDocs };

/**
 * Spec 0.7: the crystal viewer stamps each frame with its own job's CIF and trajectory
 * URL (`cifUrl` / `trajUrl`). Move them into the doc's source as source.cif_url /
 * source.traj_url (the keys loadSavedCandidates in AppProvider.jsx reads), on a new
 * object so the shared source is never mutated, and keep them out of the stored frame,
 * so a reopened run maps each crystal to its own files. A frame without either key
 * returns its doc unchanged.
 */
function withFrameFileUrls(doc) {
  const f = doc.frame;
  if (!f || !('cifUrl' in f || 'trajUrl' in f)) return doc;
  const { cifUrl, trajUrl, ...frame } = f;
  const fileUrls = {
    ...(cifUrl ? { cif_url: cifUrl } : {}),
    ...(trajUrl ? { traj_url: trajUrl } : {}),
  };
  return {
    ...doc,
    source: Object.keys(fileUrls).length > 0 ? { ...(doc.source || {}), ...fileUrls } : doc.source,
    frame,
  };
}

/**
 * Write all candidates from a run. Full candidate data is persisted:
 * frame, props, source (service, variant, model_path, weights, version),
 * formula, smiles, score, rank, diagnostics.
 */
export async function pushRunCandidates(orgId, runId, candidates) {
  if (!db || !orgId || !runId || !candidates?.length) return;
  const writes = candidates.map(c => {
    const fp    = _canonicalFingerprint(c);
    // H13: never write a doc under "undefined"/"null" — an unresolvable candidate
    // is a data bug upstream, not a doc ID.
    if (!fp) {
      console.warn('[db] pushRunCandidates: skipping candidate with no resolvable fingerprint', c.formula || c.uniqueKey || c);
      return Promise.resolve();
    }
    const docId = toDocId(fp);
    try {
      const clean = JSON.parse(JSON.stringify({
        orgId,
        runId,
        fingerprint:  fp,
        uniqueKey:    c.uniqueKey    || null,
        domain:       c.domain       || null,
        score:        c.score        ?? null,
        rank:         c.rank         ?? null,
        formula:      c.formula      || null,
        smiles:       c.smiles       || null,
        n:            c.frame?.n     || c.n || null,
        // Structural fields (crystals)
        // positions and lattice_matrix are nested arrays — stringify for Firestore
        composition:    c.composition    || c.frame?.composition || null,
        elements:       c.elements       || c.frame?.elements || null,
        positions:      _maybeStringify(c.positions || c.frame?.positions),
        lattice_abc:    c.lattice_abc    || c.frame?.lattice_abc || null,
        lattice_matrix: _maybeStringify(c.lattice_matrix || c.frame?.lattice_matrix),
        space_group:    c.space_group    || c.frame?.space_group || null,
        cif:            c.cif            || c.frame?.cif || null,
        source:       c.source ? _deepStringifyArrays(c.source) : null,
        props:        c.props  ? _deepStringifyArrays(c.props)  : {},
        // Full frame data — stringify to avoid nested-array issues
        frame:        c.frame ? JSON.stringify(c.frame) : null,
        // Materials Project match metadata
        mp_match:     c.mp_match     || null,
        // Scoring diagnostics
        diagnostics:  c.diagnostics  || null,
        // Pool-only consent marker (spec 2026-10-07 Part 0.5): written only as a
        // strict boolean true and omitted otherwise, so an ordinary save's merge
        // never clears a flag a bulk capture set. firestore.rules checks it
        // against orgs/{orgId}.dataPooling.
        ...(c.pooledCapture === true ? { pooledCapture: true } : {}),
        // Note: saved/savedBy/savedAt intentionally omitted — merge:true
        // preserves existing saved state set by setSavedFlag().
      }));
      return setDoc(
        doc(db, 'orgs', orgId, 'runs', runId, 'candidates', docId),
        clean,
        { merge: true }
      );
    } catch (_) {
      return Promise.resolve();
    }
  });
  await Promise.allSettled(writes);
}

/**
 * Load all candidates for a specific run, grouped by model into the toolResults shape:
 * { modelId: { candidates: [...], meta: { service: modelId } } }
 */
export async function loadRunCandidates(orgId, runId) {
  if (!db || !orgId || !runId) return null;
  try {
    const snap = await getDocs(collection(db, 'orgs', orgId, 'runs', runId, 'candidates'));
    if (snap.empty) return null;

    const toolResults = {};
    snap.docs.forEach(d => {
      const c = d.data();
      // Rehydrate fields that were stringified for Firestore
      c.positions = _maybeParse(c.positions);
      c.lattice_matrix = _maybeParse(c.lattice_matrix);
      if (typeof c.frame === 'string') {
        try { c.frame = JSON.parse(c.frame); } catch (_) { c.frame = null; }
      }
      const modelId = c.source?.service || 'unknown';
      if (!toolResults[modelId]) toolResults[modelId] = { candidates: [], meta: { service: modelId } };
      toolResults[modelId].candidates.push(c);
    });

    // Reconstruct `runs` array for ResultsTable compatibility.
    // Groups candidates by source.run_id and builds a minimal request/response shape.
    for (const [serviceId, entry] of Object.entries(toolResults)) {
      const byRunId = {};
      for (const c of entry.candidates) {
        const rid = c.source?.run_id || 'unknown';
        if (!byRunId[rid]) byRunId[rid] = { candidates: [], files: c.source?.files || {} };
        byRunId[rid].candidates.push(c);
      }
      const isMolecular = serviceId === 'fm4m' || serviceId === 'gt4sd';
      const isInvestigate = serviceId === 'investigate';
      entry.runs = Object.entries(byRunId).map(([rid, group]) => {
        const first = group.candidates[0] || {};
        const src = first.source || {};
        if (isInvestigate) {
          return {
            request: {
              formula: first.formula || null,
              has_cif: !!first.cif,
              lookup_source: src.lookup_source || null,
            },
            response: { run_id: rid, files: { extxyz: '__db__' } },
          };
        }
        if (isMolecular) {
          // Reconstruct FM4M/GT4SD runs with SMILES inputs for "View Molecules" button
          const smilesArr = group.candidates.map(c => c.smiles || c.source?.smiles).filter(Boolean);
          return {
            request: { inputs: smilesArr, model: src.model || serviceId },
            response: { run_id: rid, model: src.model || serviceId, canonical_smiles: smilesArr },
          };
        }
        const modelPath = src.model_path || null;
        const pretrained = modelPath ? modelPath.split('/').pop() : null;
        return {
          request: {
            pretrained_name: pretrained,
            model_path: modelPath,
            batch_size: group.candidates.length,
            num_batches: 1,
            properties: src.properties || {},
          },
          response: {
            run_id: rid,
            files: group.files || { extxyz: '__db__' }, // sentinel so "View Crystals" button renders
          },
        };
      });
    }

    return Object.keys(toolResults).length > 0 ? toolResults : null;
  } catch (e) {
    console.error('[db] loadRunCandidates failed:', e);
    return null;
  }
}

/**
 * Set the saved flag on a candidate document. Tracks who saved/unsaved it.
 */
export async function setSavedFlag(orgId, runId, fingerprint, isSaved, savedByUid) {
  if (!db || !orgId) { console.warn('[db] setSavedFlag skipped — db:', !!db, 'orgId:', orgId); return; }
  const docId = toDocId(fingerprint);
  try {
    const payload = {
      orgId,
      fingerprint,
      saved:   isSaved,
      savedBy: isSaved ? (savedByUid || null) : null,
      savedAt: isSaved ? serverTimestamp() : null,
    };
    // M12: only write runId when we actually have one. Unsave passes runId=null,
    // and `runId: runId || null` was clobbering the stored value — a later re-save
    // could then point at the wrong run and load a degraded stub.
    if (runId) payload.runId = runId;
    await setDoc(doc(db, 'orgs', orgId, 'analyses', docId), payload, { merge: true });
  } catch (e) {
    console.error('[db] setSavedFlag FAILED:', e);
  }
}

export async function setCandidateStage(orgId, fingerprint, stage, uid) {
  if (!db || !orgId) return;
  const docId = toDocId(fingerprint);
  try {
    await setDoc(
      doc(db, 'orgs', orgId, 'analyses', docId),
      { stage: stage || null, stageUpdatedBy: uid || null, stageUpdatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (e) {
    console.error('[db] setCandidateStage FAILED:', e);
  }
}

/**
 * Load all saved candidates for an org.
 * 1. Query /orgs/{orgId}/analyses where saved==true to get fingerprints + runIds
 * 2. Fetch the full candidate doc from /orgs/{orgId}/runs/{runId}/candidates/{fp}
 * 3. Load analysis kinds from /orgs/{orgId}/analyses/{fp}/kinds/*
 */
export async function loadSavedCandidates(orgId) {
  if (!db || !orgId) return [];
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'analyses'),
      where('saved', '==', true)
    );
    const snap = await getDocs(q);
    const results = await Promise.all(snap.docs.map(async d => {
      const meta = d.data();
      const fp = meta.fingerprint;
      const runId = meta.runId;
      if (!fp) return null;

      // Load the full candidate doc from the run (written by pushRunCandidates)
      let candidate = null;
      if (runId) {
        try {
          const candSnap = await getDocs(
            query(collection(db, 'orgs', orgId, 'runs', runId, 'candidates'),
              where('fingerprint', '==', fp), limit(1))
          );
          if (!candSnap.empty) {
            candidate = candSnap.docs[0].data();
            candidate.positions = _maybeParse(candidate.positions);
            candidate.lattice_matrix = _maybeParse(candidate.lattice_matrix);
            if (typeof candidate.frame === 'string') {
              try { candidate.frame = JSON.parse(candidate.frame); } catch (_) { candidate.frame = null; }
            }
          }
        } catch (_) { /* candidate doc may not exist */ }
      }

      // Load all analysis kinds
      const kindsSnap = await getDocs(collection(db, 'orgs', orgId, 'analyses', d.id, 'kinds'));
      const analyses = {};
      kindsSnap.docs.forEach(k => {
        const { kind, data } = k.data();
        if (kind && data) analyses[kind] = _deepParseArrays(data);
      });

      if (candidate) {
        candidate.analyses = analyses;
        candidate.savedAt = meta.savedAt || null;
        candidate.savedBy = meta.savedBy || null;
        candidate.stage = meta.stage || null;
        return candidate;
      }

      // Fallback: derive identity from fingerprint if candidate doc is missing
      let formula = null, smiles = null, lattice_abc = null;
      if (fp.startsWith('mol_')) {
        smiles = fp.slice(4);
      } else {
        const lMatch = fp.match(/^(.+?)_L:([\d.]+)_([\d.]+)_([\d.]+)$/);
        if (lMatch) {
          formula = lMatch[1];
          lattice_abc = [parseFloat(lMatch[2]), parseFloat(lMatch[3]), parseFloat(lMatch[4])];
        } else {
          const nMatch = fp.match(/^(.+?)_N:(\d+)$/);
          if (nMatch) formula = nMatch[1];
        }
      }
      if (!formula && !smiles) return null;
      return { fingerprint: fp, formula, smiles, lattice_abc, domain: smiles ? 'molecular' : 'inorganic_crystal', savedAt: meta.savedAt || null, savedBy: meta.savedBy || null, stage: meta.stage || null, analyses };
    }));
    return results.filter(Boolean);
  } catch (e) {
    console.error('[db] loadSavedCandidates FAILED:', e);
    return [];
  }
}

export async function loadCandidateByFingerprint(orgId, fingerprint) {
  if (!db || !orgId || !fingerprint) return null;
  const docId = toDocId(fingerprint);
  try {
    const metaSnap = await getDoc(doc(db, 'orgs', orgId, 'analyses', docId));
    if (!metaSnap.exists()) return null;
    const meta = metaSnap.data();
    if (!meta.saved) return null;

    let candidate = null;
    if (meta.runId) {
      try {
        const candSnap = await getDocs(
          query(collection(db, 'orgs', orgId, 'runs', meta.runId, 'candidates'),
            where('fingerprint', '==', fingerprint), limit(1))
        );
        if (!candSnap.empty) {
          candidate = candSnap.docs[0].data();
          candidate.positions = _maybeParse(candidate.positions);
          candidate.lattice_matrix = _maybeParse(candidate.lattice_matrix);
          if (typeof candidate.frame === 'string') {
            try { candidate.frame = JSON.parse(candidate.frame); } catch (_) { candidate.frame = null; }
          }
        }
      } catch (_) {}
    }

    const kindsSnap = await getDocs(collection(db, 'orgs', orgId, 'analyses', docId, 'kinds'));
    const analyses = {};
    kindsSnap.docs.forEach(k => {
      const { kind, data } = k.data();
      if (kind && data) analyses[kind] = _deepParseArrays(data);
    });

    const noteSnap = await getDoc(doc(db, 'orgs', orgId, 'notes', docId));
    let notes = [];
    if (noteSnap.exists()) {
      const nd = noteSnap.data();
      if (Array.isArray(nd.messages)) {
        notes = nd.messages;
      } else {
        // Migrate old entries format
        let entries = nd.entries || {};
        if (Object.keys(entries).length === 0) {
          for (const key of Object.keys(nd)) {
            if (key.startsWith('entries.')) entries[key.slice('entries.'.length)] = nd[key];
          }
        }
        notes = Object.entries(entries).map(([uid, e]) => ({
          id: `migrated_${uid}`, uid, userEmail: e.userEmail || null,
          text: e.text || '', createdAt: e.updatedAt || new Date().toISOString(),
        }));
      }
    }

    if (candidate) {
      candidate.analyses = analyses;
      candidate.notes = notes;
      candidate.savedAt = meta.savedAt || null;
      candidate.savedBy = meta.savedBy || null;
      candidate.stage = meta.stage || null;
      candidate.runId = meta.runId || null;
      return candidate;
    }

    let formula = null, smiles = null, lattice_abc = null;
    if (fingerprint.startsWith('mol_')) {
      smiles = fingerprint.slice(4);
    } else {
      const lMatch = fingerprint.match(/^(.+?)_L:([\d.]+)_([\d.]+)_([\d.]+)$/);
      if (lMatch) {
        formula = lMatch[1];
        lattice_abc = [parseFloat(lMatch[2]), parseFloat(lMatch[3]), parseFloat(lMatch[4])];
      } else {
        const nMatch = fingerprint.match(/^(.+?)_N:(\d+)$/);
        if (nMatch) formula = nMatch[1];
      }
    }
    if (!formula && !smiles) return null;
    const frame = smiles ? { type: 'molecule', smiles, formula } : { formula, lattice_abc, n: 0 };
    return { fingerprint, formula, smiles, lattice_abc, frame, domain: smiles ? 'molecular' : 'inorganic_crystal', savedAt: meta.savedAt || null, stage: meta.stage || null, analyses, notes };
  } catch (e) {
    console.error('[db] loadCandidateByFingerprint FAILED:', e);
    return null;
  }
}

// ── Analysis / report cache ───────────────────────────────────────────────────
// /orgs/{orgId}/analyses/{fp}/kinds/{kind}
// Analyses are org-scoped (cross-run) and track who initiated each one.

/**
 * Save analysis results for a candidate.
 * @param {string} orgId
 * @param {string} fingerprint - candidate fingerprint
 * @param {Object} analysesMap - { kind: data }
 * @param {Object} [opts] - { uid, userEmail, runId } for attribution
 */
export async function saveAnalyses(orgId, fingerprint, analysesMap, { uid, userEmail, runId } = {}) {
  if (!db || !orgId || !analysesMap) return;
  const docId = toDocId(fingerprint);
  const writes = Object.entries(analysesMap).map(([kind, data]) => {
    if (!data) return Promise.resolve();
    try {
      const clean = _deepStringifyArrays(JSON.parse(JSON.stringify(data)));
      return setDoc(
        doc(db, 'orgs', orgId, 'analyses', docId, 'kinds', kind),
        {
          kind,
          data: clean,
          initiatedBy:      uid || null,
          initiatedByEmail: userEmail || null,
          runId:            runId || null,
          updatedAt:        serverTimestamp(),
        }
      ).catch(e => {
        console.error(`[db] saveAnalyses setDoc failed for kind=${kind} fp=${fingerprint}:`, e.message || e);
      });
    } catch (e) {
      console.error(`[db] saveAnalyses serialization failed for kind=${kind}:`, e.message || e);
      return Promise.resolve();
    }
  });
  await Promise.allSettled(writes);
}

/**
 * Load all analyses for a candidate (by fingerprint).
 */
export async function loadAnalyses(orgId, fingerprint) {
  if (!db || !orgId) return {};
  const docId = toDocId(fingerprint);
  const snap = await getDocs(collection(db, 'orgs', orgId, 'analyses', docId, 'kinds'));
  if (!snap.empty) {
    const map = {};
    snap.docs.forEach(d => { const { kind, data } = d.data(); map[kind] = _deepParseArrays(data); });
    return map;
  }
  return {};
}

// ── Candidate notes (chat-style message thread) ──────────────────────────────
// /orgs/{orgId}/notes/{fp}
// Each note doc stores an append-only messages array.
// Messages can be edited or deleted by the author.

function _genMsgId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

/**
 * Add a new message to a candidate's note thread.
 * Returns the message object (with generated id) for optimistic UI.
 */
export async function addNoteMessage(orgId, fingerprint, { uid, userEmail, text }) {
  if (!db || !orgId || !uid || !text) return null;
  const docId = toDocId(fingerprint);
  const ref = doc(db, 'orgs', orgId, 'notes', docId);
  const msg = { id: _genMsgId(), uid, userEmail: userEmail || null, text, createdAt: new Date().toISOString() };

  try {
    await runTransaction(db, async (txn) => {
      const snap = await txn.get(ref);
      if (snap.exists()) {
        const data = snap.data();
        let messages = data.messages || [];
        // Migrate old entries format into messages array on first new write
        if (messages.length === 0 && data.entries) {
          let entries = data.entries;
          if (Object.keys(entries).length === 0) {
            for (const key of Object.keys(data)) {
              if (key.startsWith('entries.')) entries[key.slice('entries.'.length)] = data[key];
            }
          }
          messages = Object.entries(entries).map(([eUid, e]) => ({
            id: `migrated_${eUid}`, uid: eUid, userEmail: e.userEmail || null,
            text: e.text || '', createdAt: e.updatedAt || new Date().toISOString(),
          }));
          messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        }
        messages.push(msg);
        txn.update(ref, { messages, updatedAt: serverTimestamp() });
      } else {
        txn.set(ref, { fingerprint, messages: [msg], updatedAt: serverTimestamp() });
      }
    });
  } catch (e) {
    console.error('[db] addNoteMessage failed:', e);
  }
  return msg;
}

/**
 * Edit an existing message (by id) in a candidate's note thread.
 */
export async function editNoteMessage(orgId, fingerprint, messageId, text) {
  if (!db || !orgId || !messageId) return;
  const docId = toDocId(fingerprint);
  const ref = doc(db, 'orgs', orgId, 'notes', docId);

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref);
    if (!snap.exists()) return;
    const messages = snap.data().messages || [];
    const idx = messages.findIndex(m => m.id === messageId);
    if (idx === -1) return;
    messages[idx] = { ...messages[idx], text, editedAt: new Date().toISOString() };
    txn.update(ref, { messages, updatedAt: serverTimestamp() });
  });
}

/**
 * Delete a message (by id) from a candidate's note thread.
 */
export async function deleteNoteMessage(orgId, fingerprint, messageId) {
  if (!db || !orgId || !messageId) return;
  const docId = toDocId(fingerprint);
  const ref = doc(db, 'orgs', orgId, 'notes', docId);

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(ref);
    if (!snap.exists()) return;
    const messages = (snap.data().messages || []).filter(m => m.id !== messageId);
    if (messages.length === 0) {
      txn.delete(ref);
    } else {
      txn.update(ref, { messages, updatedAt: serverTimestamp() });
    }
  });
}

/**
 * Load all notes for an org.
 * Returns { fingerprint: [{ id, uid, userEmail, text, createdAt, editedAt? }, ...] }
 */
export async function loadNotes(orgId) {
  if (!db || !orgId) return {};
  const snap = await getDocs(collection(db, 'orgs', orgId, 'notes'));
  const map = {};
  snap.docs.forEach(d => {
    const data = d.data();
    if (!data.fingerprint) return;

    // New format: messages array
    if (Array.isArray(data.messages) && data.messages.length > 0) {
      map[data.fingerprint] = data.messages;
      return;
    }

    // Migration: old entries format → messages array
    let entries = data.entries || {};
    if (Object.keys(entries).length === 0) {
      for (const key of Object.keys(data)) {
        if (key.startsWith('entries.')) {
          const uid = key.slice('entries.'.length);
          entries[uid] = data[key];
        }
      }
    }
    if (Object.keys(entries).length > 0) {
      const messages = Object.entries(entries).map(([uid, entry]) => ({
        id: `migrated_${uid}`,
        uid,
        userEmail: entry.userEmail || null,
        text: entry.text || '',
        createdAt: entry.updatedAt || new Date().toISOString(),
      }));
      messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      map[data.fingerprint] = messages;
    }
  });
  return map;
}

// ── Test-only exports ──────────────────────────────────────────────────────────
export const _testing = {
  _removeNestedArrays,
  _stripToolResults,
  _restoreNestedArrays,
  _canonicalFingerprint,
  _maybeStringify,
  _deepStringifyArrays,
  _maybeParse,
  framesToCandidateDocs,
};
